(ns hai.notes.form
  "The note-editing pieces every note editor shares — the detail page, the
   edit / create modals, the home pad, the sidebar: [[note-form]], the
   composer staging the text and its tags/links locally until its one Save,
   and [[note-extras]], the tags/links sections with their open/close state
   and searches wired in, which the read-only surfaces render on their own.
   Mirrors [[hai.tasks.form]]."
  (:require
   [hai.entity.draft :as draft]
   [hai.entity.form :as entity-form]
   [hai.entity.link-combobox :as link-combobox]
   [hai.notes.sections :as sections]
   [hai.tags.combobox :as tag-combobox]
   [hai.ui.keyboard :as keyboard]
   [hai.ui.rf-sugar :refer [<s >e]]
   [reagent.core :as r]))

(defn from-note
  "A note as a draft payload: its text and every chip it carries. What a
   composer is seeded with, and what it measures having something to save
   against."
  [note]
  {:text     (or (:note/text note) "")
   :urls     (vec (keep :activity/url (:note/activities note)))
   :contacts (vec (:note/contacts note))
   :notes    (vec (:note/notes note))
   :tasks    (vec (:note/tasks note))
   :tags     (vec (:note/tags note))
   :spaces   (vec (:entity/spaces note))})

(defn unfiled?
  "Whether `draft` holds anything the note as filed doesn't — what enables Save
   and Cancel, and what a host asks about before letting the writing go."
  [draft note]
  (not= draft (from-note note)))

(defn note-extras
  "The tags/links sections with their open/close state and the tag search
   wired in. Values are plain props — deref them in the host's render. Adding
   a tag or link collapses its section — the adder hides, reopened by its ＋
   control. A note's spaces share the tag row: `on-add-space`/`on-remove-space`
   (optional) each take the full picked space map, mirroring `on-add-tag`."
  [{:keys [editing? tags spaces link-urls contacts linked-notes linked-tasks note-uuid
           on-add-tag on-remove-tag on-add-space on-remove-space on-add-link on-remove-link
           on-add-contact on-remove-contact on-add-note on-remove-note
           on-add-task on-remove-task on-open-entity]}]
  (r/with-let [!open-sections (r/atom #{})]
    [sections/note-extras
     {:editing?             editing?
      :tags                 tags
      :spaces               spaces
      :link-urls            link-urls
      :contacts             contacts
      :linked-notes         linked-notes
      :linked-tasks         linked-tasks
      :note-uuid            note-uuid
      :open-set             @!open-sections
      :open-section!        (fn [section] (swap! !open-sections conj section))
      :close-section!       (fn [section] (swap! !open-sections disj section))
      :on-add-tag           on-add-tag
      :on-remove-tag        on-remove-tag
      ;; Spaces stage like tags — the composer keeps the full picked map.
      :on-add-space         on-add-space
      :on-remove-space      on-remove-space
      :on-add-link          on-add-link
      :on-remove-link       on-remove-link
      ;; Adds hand the host the full picked entity — it stages the chip, so it
      ;; needs the display fields. Removes identify by uuid.
      :on-add-contact       on-add-contact
      :on-remove-contact    (when on-remove-contact
                              (fn [contact] (on-remove-contact (:contact/uuid contact))))
      :on-add-note          on-add-note
      :on-remove-note       (when on-remove-note
                              (fn [note] (on-remove-note (:note/uuid note))))
      :on-add-task          on-add-task
      :on-remove-task       (when on-remove-task
                              (fn [task] (on-remove-task (:task/uuid task))))
      :on-open-entity       on-open-entity
      :render-tag-search    tag-combobox/search-slot
      ;; One picker covers all three families, so any of the add handlers
      ;; being wired means the host wants the searchable adder.
      :render-entity-search (when (or on-add-contact on-add-note on-add-task)
                              link-combobox/entity-search)}]))

(defn note-form
  "The composer: the note's text over its tags/links sections, all of it held
   in the draft at `draft-id` ([[hai.entity.draft]]). The host seeds that draft
   and two composers may share one — the home pad and the expanded editor it
   opens do, which is what makes them one editing session rather than a copy
   handed between them.

   Nothing saves until Save — or ⌘/Ctrl+↵ in the text — which hands `on-save`
   the draft `{:text :urls :contacts :notes :tasks :tags :spaces}` in one go; the host
   closes or re-seeds from there. `note` is the note as filed: what the draft
   is measured against to know whether there is anything to save, and what
   keeps the note out of its own link picker.

   Save and Cancel are always there, disabled while the composer holds the note
   exactly as it stands. `saving?` is the host's in-flight state: Save spins and
   holds while it does, so one save goes out at a time. `on-cancel` is what
   Cancel does — put the draft back to the note as filed; `show-hint?` puts the
   chord in the Save (off for touch); `footer-start` is what the host wants at
   the far left of the footer — the share controls. `spaces?` (default false)
   turns on the spaces half of the tag row. Renders
   `[:textarea.body …]` for the host to style. Mirrors
   [[hai.tasks.form/task-form]]."
  [{:keys [draft-id note asking placeholder auto-focus? show-hint? footer-start
           spaces? saving? on-save on-cancel on-open-entity]}]
  (r/with-let [!caret (atom nil)
               !caret-restored? (atom false)
               ;; A stable ref, and once per mount: the caret the draft carries
               ;; is where the writer left the other composer, not somewhere to
               ;; drag them back to on every keystroke.
               attach-textarea! (fn [el]
                                  (when (and el @!caret (not @!caret-restored?))
                                    (reset! !caret-restored? true)
                                    (.focus el)
                                    (set! (.-selectionStart el) (first @!caret))
                                    (set! (.-selectionEnd el) (second @!caret))))
               ;; Only from the focused field: the other composer on this draft
               ;; is still mounted behind the dialog, and its textarea fires
               ;; select as the shared text changes under it.
               remember-caret! (fn [e]
                                 (let [el (.-target e)]
                                   (when (= el (.-activeElement js/document))
                                     (>e [::draft/caret-moved draft-id
                                          [(.-selectionStart el) (.-selectionEnd el)]]))))]
    (let [{:keys [text urls contacts notes tasks tags spaces] :as draft}
          @(<s [::draft/draft draft-id])
          _ (reset! !caret @(<s [::draft/caret draft-id]))
          change! (fn [changes] (>e [::draft/change draft-id changes]))
          ;; Measured against the note as filed, so a composer handed unsaved
          ;; contents has something to save from its first render, and one that
          ;; has just saved has nothing again without being re-seeded.
          unfiled? (unfiled? draft note)
          save! (fn []
                  (when (and unfiled? (not saving?))
                    (on-save draft)))]
      [:<>
       [:textarea.body
        (cond-> {:value (or text "")
                 :aria-label "Edit note"
                 :ref attach-textarea!
                 :on-select remember-caret!
                 :on-change (fn [e] (change! {:text (.. e -target -value)}))
                 :on-key-down (fn [e]
                                (when (keyboard/submit-shortcut? e)
                                  (.preventDefault e)
                                  (save!)))}
          placeholder (assoc :placeholder placeholder)
          auto-focus? (assoc :auto-focus true))]
       [note-extras
        {:editing?          true
         :tags              tags
         ;; Space chips always show — the server only resolves spaces the viewer
         ;; can see. `spaces?` (admin + developer-features) gates only the
         ;; add/remove controls below, not the display.
         :spaces            spaces
         :link-urls         urls
         :contacts          contacts
         :linked-notes      notes
         :linked-tasks      tasks
         :note-uuid         (:note/uuid note)
         :on-add-tag        (fn [tag] (change! {:tags (conj (vec tags) tag)}))
         :on-remove-tag     (fn [tag]
                              (change! {:tags (vec (remove #(= % tag) tags))}))
         ;; Spaces stage like tags — the full picked map, committed on Save.
         :on-add-space      (when spaces?
                              (fn [space] (change! {:spaces (conj (vec spaces) space)})))
         :on-remove-space   (when spaces?
                              (fn [space]
                                (change! {:spaces (vec (remove #(= (:space/uuid %) (:space/uuid space))
                                                               spaces))})))
         :on-add-link       (fn [link-url]
                              (when-not (some #{link-url} urls)
                                (change! {:urls (conj (vec urls) link-url)})))
         :on-remove-link    (fn [link-url]
                              (change! {:urls (vec (remove #{link-url} urls))}))
         :on-add-contact    (fn [contact]
                              (change! {:contacts (conj (vec contacts) contact)}))
         :on-remove-contact (fn [contact-uuid]
                              (change! {:contacts (vec (remove #(= contact-uuid (:contact/uuid %))
                                                               contacts))}))
         :on-add-note       (fn [linked-note]
                              (change! {:notes (conj (vec notes) linked-note)}))
         :on-remove-note    (fn [linked-uuid]
                              (change! {:notes (vec (remove #(= linked-uuid (:note/uuid %))
                                                            notes))}))
         :on-add-task       (fn [task] (change! {:tasks (conj (vec tasks) task)}))
         :on-remove-task    (fn [task-uuid]
                              (change! {:tasks (vec (remove #(= task-uuid (:task/uuid %))
                                                            tasks))}))
         :on-open-entity    on-open-entity}]
       [entity-form/footer
        (when footer-start
          [:div.footer-start footer-start])
        (if asking
          [entity-form/confirm asking]
          [entity-form/actions
           {:unfiled?  unfiled?
            :saving?   saving?
            :shortcut  (when show-hint? (entity-form/chord "↵"))
            :on-cancel on-cancel
            :on-save   save!}])]])))
